<template>
    <flex-element
        v-on-clickaway="collapse"
        :element-type="elementType"
        class="f-flex f-relative"
        :class="stateClasses"
        class-default-inline="f-flex-col f-z-10 f-max-w-1/1"
    >
        <div class="f-flex h-1/1">
            <a-link
                role="menuitem"
                :aria-haspopup="hasChildren"
                :aria-expanded="isExpanded"
                class-default="f-block f-self-center f-px-1 f-py-0.5 f-w-max-content"
                :display-name="`${ displayName } - Link`"
                v-bind="element(1)"
                :is-disabled="hasChildren"
                :is-active="isExpanded"
                @match="onLinkUrlMatch"
                @element-updated="elementUpdated"
                @click.native="onLinkClick"
                @mouseenter.native="onMouseEnterLink"
                @mouseleave.native="onMouseLeaveLink"
            />

            <a-icon
                v-if="isIconVisible"
                class-default="f-self-center f-pl-0.25 f-pr-0.75"
                :display-name="`${ displayName } - Icon`"
                :icon="icon"
                set="fal"
                v-bind="element(3)"
                @element-updated="elementUpdated"
                @click.native="onLinkClick"
            />
        </div>

        <nav-dropdown-content
            :direction="direction"
            :is-expanded="isExpanded"
            :speed="speed"
            :is-full-width="isFullWidth"
            :is-mobile="isMobile"
            v-bind="element(2)"
            @count="onContentChange"
            @mouse-enter="onMouseEnterDropdown"
            @mouse-leave="onMouseLeaveDropdown"
            @element-updated="elementUpdated"
        />
    </flex-element>
</template>

<script>
import { mapState } from 'vuex';
import uniqid from 'uniqid';
import { mixin as clickaway } from 'vue-clickaway';

import getParentElement from '@/utils/get-parent-element';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { STATE_CLASSES } from '@/data/constants/classes';

const CLICK_EVENT = 'flex::nav-link::click';

const ANCHOR_TARGET_ATTR = 'dropdown-anchor';
const ANCHOR_TARGET_ID_ATTR = 'dropdown-anchor-id';
const DIRECTION_ATTR = 'dropdown-direction';
const ICON_ATTR = 'dropdown-icon';
const ICON_DISPLAY_ATTR = 'dropdown-icon-display';
const SPEED_ATTR = 'dropdown-speed';
const TRIGGER_ATTR = 'dropdown-trigger';

const DEFAULT_DIRECTION = 'down';
const DEFAULT_ICON_DISPLAY = 'toggle';
const DEFAULT_SPEED = '0.2'; // in seconds
const DEFAULT_TRIGGER = 'click';

export default {
    name: 'nav-dropdown',
    mixins: [baseOrganism, clickaway, listenOnRootMixin],

    props: {
        displayName: {
            type: String,
            default: 'Nav Dropdown',
        },

        isMobile: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            cssDisplay: 'block',
            hasChildren: false,
            isExpanded: false,
            isMatchingLink: false,
            itemId: uniqid(),
            mouseOverDropdown: false,
            speed: DEFAULT_SPEED,
            targetElement: null,
            trigger: DEFAULT_TRIGGER,
        };
    },

    computed: {
        ...mapState({
            changedOption: state => state.page.changedOption,
        }),

        anchorTarget() {
            return this.getElementData(ANCHOR_TARGET_ATTR);
        },

        direction() {
            return this.getElementData(DIRECTION_ATTR) || DEFAULT_DIRECTION;
        },

        dropdownAnchorId() {
            return this.getElementData(ANCHOR_TARGET_ID_ATTR);
        },

        icon() {
            switch (true) {
                case !this.icons:
                    return this.isExpanded ? 'chevron-up' : 'chevron-down';

                case this.iconDisplay === 'toggle' && this.isExpanded:
                    return this.icons.expanded;

                case this.iconDisplay === 'toggle' && !this.isExpanded:
                    return this.icons.collapsed;

                case this.iconDisplay === 'show':
                    return this.icons.collapsed;

                default:
                    return '';
            }
        },

        icons() {
            const iconData = this.getElementData(ICON_ATTR);

            if (!iconData) return null;

            return {
                collapsed: iconData.collapsed,
                expanded: iconData.expanded,
            };
        },

        iconDisplay() {
            return this.getElementData(ICON_DISPLAY_ATTR) || DEFAULT_ICON_DISPLAY;
        },

        isFullWidth() {
            return !!this.anchorTarget;
        },

        isIconVisible() {
            return this.iconDisplay === 'show' || (this.iconDisplay === 'toggle' && this.hasChildren);
        },

        stateClasses() {
            return {
                [STATE_CLASSES.MATCHING]: this.isMatchingLink,
                [STATE_CLASSES.SELECTED]: this.isExpanded,
            };
        },
    },

    watch: {
        customAttributesNew: {
            handler() {
                this.setData();
            },
            deep: true,
        },

        changedOption({ option, value }) {
            if (option === 'nav-dropdown-anchor') {
                const { isEnabled } = value;

                if (isEnabled) {
                    this.setAnchorStyles();
                }
            }
        },
    },

    created() {
        this.listenOnRoot(CLICK_EVENT, this.onClickEvent);
        this.setData();
    },

    async mounted() {
        await this.$nextTick();

        if (this.isFullWidth) {
            this.setAnchorStyles();
        }
    },

    methods: {
        setData() {
            this.speed = this.getElementData(SPEED_ATTR) || DEFAULT_SPEED;
            this.trigger = this.getElementData(TRIGGER_ATTR) || DEFAULT_TRIGGER;
        },

        setAnchorStyles() {
            if (this.targetElement) {
                this.targetElement.style.removeProperty('transform');
            }

            // n.b. users are only able to select the "self" and "parent" options for nav dropdowns that are inside of a collection
            switch (this.anchorTarget) {
                case 'self': {
                    const collectionElement = getParentElement(this);

                    if (collectionElement) {
                        this.targetElement = collectionElement.$el;
                    } else {
                        console.warn('Unable to set the dropdown anchor using the "self" option.');
                    }

                    break;
                }

                case 'parent': {
                    const collectionParentElement = getParentElement(this, 2);

                    if (collectionParentElement) {
                        this.targetElement = collectionParentElement.$el;
                    } else {
                        console.warn('Unable to set the dropdown anchor using the "parent" option.');
                    }

                    break;
                }

                default: {
                    this.targetElement = document.getElementById(this.dropdownAnchorId);

                    if (!this.targetElement) {
                        console.warn(`Unable to set the dropdown anchor. An element with an ID of "${ this.dropdownAnchorId }" could not be found.`);
                    }
                }
            }

            if (!this.targetElement) return;

            this.targetElement.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
        },

        onClickEvent(id) {
            if (id !== this.itemId) this.collapse();
        },

        onLinkClick() {
            if (this.trigger === 'click' || this.isMobile) this.toggle();
        },

        onMouseEnterLink() {
            if (this.trigger === 'hover') this.expand();
        },

        onMouseEnterDropdown() {
            this.mouseOverDropdown = true;
        },

        onMouseLeaveLink() {
            // small timeout to give the nav dropdown event time to emit
            window.setTimeout(() => {
                if (this.trigger === 'hover' && !this.mouseOverDropdown) {
                    this.collapse();
                }
            }, 100);
        },

        onMouseLeaveDropdown() {
            if (this.trigger === 'hover') this.collapse();
        },

        onContentChange(count) {
            this.hasChildren = !!count;
        },

        onLinkUrlMatch(isMatching) {
            this.isMatchingLink = isMatching;
        },

        toggle() {
            this.isExpanded = !this.isExpanded;
            this.$root.$emit(CLICK_EVENT, this.itemId);
        },

        expand() {
            this.isExpanded = true;
        },

        collapse() {
            this.isExpanded = false;
            this.mouseOverDropdown = false;
        },
    },
};
</script>
